import React, { useState, useEffect } from "react";
import QuarterForm from "../components/QuarterForm";
import "./AdminPanel.css";

function AdminPanel() {
  const [quarters, setQuarters] = useState([]);
  const [editingQuarter, setEditingQuarter] = useState(null);
  const [repairWorks, setRepairWorks] = useState([]);
  const [showRepairForm, setShowRepairForm] = useState(false);
  const [editingRepair, setEditingRepair] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [showVacantPopup, setShowVacantPopup] = useState(false);
  
  // Get API base URL from environment variables
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const blockNamesList = [
  "Reserve Police Line",
  "Traffic Police Line",
  "K D Line",
  "Police Office Complex",
  "PS Railbazaar",
  "PS Govindnagar",
  "PS Swaroopnagar",
  "PS Nazirabad",
  "RK Chauki Nazirabad",
  "PS Amrapur",
  "PS Fazalganj",
  "PS Gwaltoli",
  "PS Sisamau",
  "PS Chamanganj",
  "PS Bajriya",
  "PS Anwarganj",
  "PS Raipurva",
  "Chauki Coperganj Rajpurva",
  "PS Bapupurva",
  "PS Juhi",
  "PS Cantt",
  "PS Chakeri",
  "Friend Colony Chakeri",
  "PS Barra",
  "PS Kotwali",
  "PS Feelkhana (Patkapur Chauki)",
  "PS Harvanshmohal",
  "PS Kalyanpur",
  "Kalyanpur Zone Residence",
  "PS Panki",
  "PS Bithoor",
  "PS Nawabganj",
  "PS Ghatampur",
  "PS Chaubepur",
  "PS Shivrajpur",
  "PS Sachedi"
];
  
const formatQuarterLabel = (q) => {
  const index = q.block.charCodeAt(0) - 65;
  const blockName = blockNamesList[index] || q.block;
  const quarterNumber = q.quarter_no.split("-")[1];

  return `${blockName} - Type ${q.type} - ${quarterNumber} - ${
    isQuarterVacant(q) ? "Vacant" : q.resident
  }`;
};
  const [repairForm, setRepairForm] = useState({
    quarter_id: "",
    category: "minor",
    description: "",
    status: "open"
  });
  
  const [showQuarterUpdateForm, setShowQuarterUpdateForm] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    id: "",
    quarter_no: "",
    block: "",
    type: "",
    rank: "",
    resident: "",
    pno_no: "",
    mobile_no: "",
    allotment_date: "",
    allotment_year: ""
  });

  // Load quarters and repairs on component mount
  useEffect(() => {
    fetchQuarters();
    fetchRepairs();
  }, []);

  const fetchQuarters = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/quarters`);
      if (!response.ok) throw new Error('Failed to fetch quarters');
      const data = await response.json();
      setQuarters(data);
    } catch (err) {
      console.error('Error fetching quarters:', err);
      setError('Failed to load quarters');
    } finally {
      setLoading(false);
    }
  };

  const fetchRepairs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/repairs/all`);
      if (!response.ok) throw new Error('Failed to fetch repairs');
      const data = await response.json();
      setRepairWorks(data);
    } catch (err) {
      console.error('Error fetching repairs:', err);
    }
  };

  const handleAdd = async (quarter) => {
    try {
      console.log('Sending quarter data:', quarter);
      
      const response = await fetch(`${API_BASE_URL}/api/quarters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quarter),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        
        if (errorData.code === 'DUPLICATE_QUARTER') {
          alert(`Error: ${errorData.error}\n\nThe quarter number "${quarter.quarter_no}" is already in use. Please use a different quarter number.`);
        } else {
          alert(`Failed to add quarter: ${errorData.error || 'Unknown error'}`);
        }
        return;
      }
      
      const result = await response.json();
      setQuarters([...quarters, result.quarter]);
      alert('Quarter added successfully!');
      
    } catch (err) {
      console.error('Network error:', err);
      alert('Network error. Please check your connection and try again.');
    }
  };

  const handleUpdateQuarter = async (updatedQuarter) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/quarters/${updatedQuarter.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedQuarter),
      });
      
      if (!response.ok) throw new Error('Failed to update quarter');
      
      const updated = await response.json();
      setQuarters(quarters.map(q => q.id === updated.id ? updated : q));
      setShowQuarterUpdateForm(false);
      setUpdateForm({
        id: "",
        quarter_no: "",
        block: "",
        type: "",
        rank: "",
        resident: "",
        pno_no: "",
        mobile_no: "",
        allotment_date: "",
        allotment_year: ""
      });
      alert('Quarter updated successfully!');
    } catch (err) {
      console.error('Error updating quarter:', err);
      alert('Failed to update quarter. Please try again.');
    }
  };

  const handleDeleteQuarter = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/quarters/${id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) throw new Error('Failed to delete quarter');
      
      setQuarters(quarters.filter(q => q.id !== id));
      alert('Quarter deleted successfully!');
    } catch (err) {
      console.error('Error deleting quarter:', err);
      alert('Failed to delete quarter. Please try again.');
    }
  };

  const handleEditQuarter = (quarter) => {
    setUpdateForm({
      ...quarter,
      allotment_date: quarter.allotment_date ? quarter.allotment_date.split('T')[0] : ""
    });
    setShowQuarterUpdateForm(true);
  };

  const handleUpdateInputChange = (e) => {
    const { name, value } = e.target;
    setUpdateForm({
      ...updateForm,
      [name]: value
    });
  };

  const handleUpdateSubmit = (e) => {
    e.preventDefault();
    handleUpdateQuarter(updateForm);
  };

  // Image handling functions
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file types
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const validFiles = files.filter(file => validTypes.includes(file.type));
    
    if (validFiles.length !== files.length) {
      alert('Some files were skipped. Only JPEG, PNG, and GIF images are allowed.');
    }
    
    // Limit number of images (e.g., max 5)
    const maxImages = 5;
    const currentCount = selectedImages.length;
    const remainingSlots = maxImages - currentCount;
    
    if (validFiles.length > remainingSlots) {
      alert(`You can only upload ${maxImages} images in total. ${remainingSlots} slots remaining.`);
      validFiles.splice(remainingSlots);
    }
    
    setSelectedImages([...selectedImages, ...validFiles]);
    
    // Create image previews
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const resetImageSelection = () => {
    setSelectedImages([]);
    setImagePreviews([]);
  };

  // Repair work handlers
  const handleRepairSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('quarter_id', repairForm.quarter_id);
      formData.append('category', repairForm.category);
      formData.append('description', repairForm.description);
      formData.append('status', repairForm.status);
      
      // Append images
      selectedImages.forEach((image) => {
        formData.append('images', image);
      });
      
      if (editingRepair) {
        // Update existing repair
        const response = await fetch(`${API_BASE_URL}/api/repairs/${editingRepair.id}`, {
          method: 'PUT',
          body: formData,
        });
        
        if (!response.ok) throw new Error('Failed to update repair');
        
        const updatedRepair = await response.json();
        setRepairWorks(repairWorks.map(r => r.id === updatedRepair.id ? updatedRepair : r));
        alert('Repair updated successfully!');
      } else {
        // Add new repair
        const response = await fetch(`${API_BASE_URL}/api/repairs`, {
          method: 'POST',
          body: formData,
        });
        
        if (!response.ok) throw new Error('Failed to add repair');
        
        const newRepair = await response.json();
        setRepairWorks([...repairWorks, newRepair]);
        alert('Repair added successfully!');
      }
      
      // Reset form
      setRepairForm({
        quarter_id: "",
        category: "minor",
        description: "",
        status: "open"
      });
      resetImageSelection();
      setEditingRepair(null);
      setShowRepairForm(false);
      
      // Refresh repairs list
      fetchRepairs();
    } catch (err) {
      console.error('Error with repair:', err);
      alert('Failed to save repair. Please try again.');
    }
  };

  const handleEditRepair = (repair) => {
    setRepairForm({
      quarter_id: repair.quarter_id,
      category: repair.category,
      description: repair.description,
      status: repair.status
    });
    setEditingRepair(repair);
    setShowRepairForm(true);
    resetImageSelection();
  };

  const handleDeleteRepair = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/repairs/${id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) throw new Error('Failed to delete repair');
      
      setRepairWorks(repairWorks.filter(repair => repair.id !== id));
      alert('Repair deleted successfully!');
    } catch (err) {
      console.error('Error deleting repair:', err);
      alert('Failed to delete repair. Please try again.');
    }
  };

  const handleRepairInputChange = (e) => {
    const { name, value } = e.target;
    setRepairForm({
      ...repairForm,
      [name]: value
    });
  };

  // Helper function to check if quarter is vacant
  const isQuarterVacant = (quarter) => {
    if (!quarter) return true;
    
    return !quarter.resident || 
           quarter.resident.trim() === '' || 
           quarter.resident.toLowerCase() === 'vacant' ||
           quarter.resident.toLowerCase() === 'n/a';
  };
  
  const types = ["Type 1", "Type 2", "Type 3"];
  const ranks = [
  "Inspector",
  "Sub-Inspector",
  "Assistant Sub Inspector",
  "Head Constable",
  "Constable",
  "Follower",
  "Cook",
  "Sweeper",
  "OP",
  "Driver",
  "Tailor",
  "Armourer",
  "Waterman",
  "Radio Messenger",
  "Staff Nurse",
  "APO",
  "Others"
];
  const currentYear = new Date().getFullYear();

const years = Array.from(
  { length: currentYear - 1947 + 1 },
  (_, i) => currentYear - i
);

  // Group repairs by category
  const groupedRepairs = {
    minor: repairWorks.filter(r => r.category === 'minor'),
    major: repairWorks.filter(r => r.category === 'major'),
    other: repairWorks.filter(r => r.category === 'other')
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading-spinner">
          <p>Loading admin data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <h1 className="admin-header">Admin Panel</h1>
      
      <div className="admin-sections">
        {/* Quarter Management Section */}
        <div className="quarter-section">
          <div className="section-header">
            <h2>Quarter Management</h2>
            <button 
              className="btn btn-secondary"
              onClick={() => setShowQuarterUpdateForm(!showQuarterUpdateForm)}
            >
              {showQuarterUpdateForm ? 'Cancel Update' : 'Update Existing Quarter'}
            </button>
          </div>
          
          <QuarterForm onAdd={handleAdd} />
          
          {/* Update Quarter Form */}
          {showQuarterUpdateForm && (
            <div className="update-quarter-form">
              <h3>Update Quarter Details</h3>
              <form onSubmit={handleUpdateSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Select Quarter to Update:</label>
                    <select 
                      value={updateForm.id} 
                      onChange={(e) => {
                        const quarter = quarters.find(q => q.id === parseInt(e.target.value));
                        if (quarter) {
                          setUpdateForm({
                            ...quarter,
                            allotment_date: quarter.allotment_date ? quarter.allotment_date.split('T')[0] : ""
                          });
                        }
                      }}
                      required
                    >
                      <option value="">Select Quarter</option>
                      {quarters.map(quarter => (
                        <option key={quarter.id} value={quarter.id}>
                          {formatQuarterLabel(quarter)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Quarter Number *</label>
                    <input
                      name="quarter_no"
                      placeholder="e.g., A-1, B-2"
                      value={updateForm.quarter_no}
                      onChange={handleUpdateInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Block *</label>
                    <select
                      name="block"
                      value={updateForm.block}
                      onChange={handleUpdateInputChange}
                      required
                    >
                      <option value="">Select Block</option>
                      {blockNamesList.map((name, index) => (
  <option key={index} value={String.fromCharCode(65 + index)}>
    {name}
  </option>
))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Quarter Type *</label>
                    <select
                      name="type"
                      value={updateForm.type}
                      onChange={handleUpdateInputChange}
                      required
                    >
                      <option value="">Select Type</option>
                      {types.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Rank</label>
                    <select
                      name="rank"
                      value={updateForm.rank || ""}
                      onChange={handleUpdateInputChange}
                    >
                      <option value="">Select Rank</option>
                      {ranks.map(rank => (
                        <option key={rank} value={rank}>{rank}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Resident Name</label>
                    <input
                      name="resident"
                      placeholder="Full name of resident (leave empty for vacant)"
                      value={updateForm.resident || ""}
                      onChange={handleUpdateInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>PNO Number</label>
                    <input
                      name="pno_no"
                      placeholder="e.g., UP12345"
                      value={updateForm.pno_no || ""}
                      onChange={handleUpdateInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Mobile Number</label>
                    <input
                      name="mobile_no"
                      placeholder="10-digit mobile number"
                      value={updateForm.mobile_no || ""}
                      onChange={handleUpdateInputChange}
                      maxLength="10"
                    />
                  </div>

                  <div className="form-group">
                    <label>Allotment Date</label>
                    <input
                      name="allotment_date"
                      type="date"
                      value={updateForm.allotment_date || ""}
                      onChange={handleUpdateInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Allotment Year</label>
                    <select
                      name="allotment_year"
                      value={updateForm.allotment_year || ""}
                      onChange={handleUpdateInputChange}
                    >
                      <option value="">Select Year</option>
                      {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    Update Quarter
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-danger"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to delete this quarter?')) {
                        handleDeleteQuarter(updateForm.id);
                        setShowQuarterUpdateForm(false);
                      }
                    }}
                  >
                    Delete Quarter
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Summary Stats */}
          <div className="quarters-summary">
            <div className="summary-grid">
              <div className="summary-item">
                <h4>Total Quarters</h4>
                <span className="summary-number">{quarters.length}</span>
              </div>
              <div className="summary-item">
                <h4>Occupied</h4>
                <span className="summary-number occupied">
                  {quarters.filter(q => !isQuarterVacant(q)).length}
                </span>
              </div>
              <div className="summary-item">
                <h4>Vacant</h4>
                <span 
                  className="summary-number vacant clickable" 
                  onClick={() => setShowVacantPopup(true)}
                  title="Click to view vacant quarters"
                >
                  {quarters.filter(q => isQuarterVacant(q)).length}
                </span>
              </div>
            </div>
            <p className="summary-note">
              View all quarters and their details in the main Dashboard
            </p>
          </div>
        </div>
        
        {/* Repair Work Section */}
        <div className="repair-section">
          <div className="section-header">
            <h2>Repair Work Management</h2>
            <button 
              className="btn btn-primary"
              onClick={() => {
                setShowRepairForm(!showRepairForm);
                if (!showRepairForm) {
                  resetImageSelection();
                }
              }}
            >
              {showRepairForm ? 'Cancel' : 'Add Repair Work'}
            </button>
          </div>
          
          {showRepairForm && (
            <form className="repair-form" onSubmit={handleRepairSubmit}>
              <h3>{editingRepair ? 'Edit Repair Work' : 'Add New Repair Work'}</h3>
              
              <div className="form-group">
                <label>Quarter:</label>
                <select
                  name="quarter_id"
                  value={repairForm.quarter_id}
                  onChange={handleRepairInputChange}
                  required
                >
                  <option value="">Select Quarter</option>
                  {quarters.map(quarter => (
                    <option key={quarter.id} value={quarter.id}>
                      {formatQuarterLabel(quarter)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Repair Category:</label>
                <select 
                  name="category" 
                  value={repairForm.category} 
                  onChange={handleRepairInputChange}
                  required
                >
                  <option value="minor">Minor</option>
                  <option value="major">Major</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  name="description"
                  value={repairForm.description}
                  onChange={handleRepairInputChange}
                  placeholder="Describe the repair work..."
                  required
                />
              </div>

              <div className="form-group">
                <label>Status:</label>
                <select 
                  name="status" 
                  value={repairForm.status} 
                  onChange={handleRepairInputChange}
                  required
                >
                  <option value="open">Open</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              {/* Image Upload Section */}
              <div className="form-group">
                <label>Upload Images (Max 5):</label>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  multiple
                  onChange={handleImageSelect}
                  className="file-input"
                />
                <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                  Accepted formats: JPEG, PNG, GIF. Maximum 5 images per repair.
                </small>
              </div>

              {/* Image Previews */}
              {imagePreviews.length > 0 && (
                <div className="image-previews-container">
                  <label>Selected Images ({imagePreviews.length}/5):</label>
                  <div className="image-previews">
                    {imagePreviews.map((preview, index) => (
                      <div key={index} className="image-preview-item">
                        <img 
                          src={preview} 
                          alt={`Preview ${index + 1}`}
                        />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => removeImage(index)}
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <button type="submit" className="btn btn-primary">
                {editingRepair ? 'Update Repair' : 'Add Repair'}
              </button>
            </form>
          )}
          
          {/* Display repair works by category */}
          {['minor', 'major', 'other'].map(category => (
            <div key={category} className="repair-category">
              <h3>{category.charAt(0).toUpperCase() + category.slice(1)} Repairs ({groupedRepairs[category].length})</h3>
              
              {groupedRepairs[category].length === 0 ? (
                <p className="no-items">No {category} repairs added yet.</p>
              ) : (
                <div className="repair-list">
                  {groupedRepairs[category].map(repair => {
                    const quarter = quarters.find(q => q.id === repair.quarter_id);
                    return (
                      <div key={repair.id} className="repair-item">
                        <div className="repair-info">
                          <h4>Repair #{repair.id}</h4>
                          <p>
  <strong>Quarter:</strong>{" "}
  {quarter
    ? (() => {
        const blockLetter = quarter.block;
        const index = blockLetter.charCodeAt(0) - 65;
        const blockName = blockNamesList[index] || blockLetter;
        const quarterNumber = quarter.quarter_no?.split("-")[1];

        return `${blockName} - ${quarterNumber}`;
      })()
    : "Unknown Quarter"}
</p>
                          <p><strong>Resident:</strong> {quarter ? (isQuarterVacant(quarter) ? 'Vacant' : quarter.resident) : 'Unknown'}</p>
                          <p><strong>Description:</strong> {repair.description}</p>
                          <p><strong>Status:</strong> <span className={`status-badge ${repair.status}`}>{repair.status}</span></p>
                          <p><strong>Created:</strong> {new Date(repair.created_at).toLocaleDateString()}</p>
                          
                          {/* Display images if available */}
                          {repair.images && repair.images.length > 0 && (
                            <div className="repair-images">
                              <strong>Images ({repair.images.length}):</strong>
                              <div className="repair-images-grid">
                                {repair.images.map((imagePath, idx) => {
                                  // Handle both formats: "/uploads/repairs/..." or "uploads/repairs/..."
                                  const imageUrl = imagePath.startsWith('/') 
                                    ? `${API_BASE_URL}${imagePath}`
                                    : `${API_BASE_URL}/${imagePath}`;
                                  
                                  return (
                                    <div key={idx} className="repair-image-thumbnail">
                                      <img
                                        src={imageUrl}
                                        alt={`Repair ${repair.id} - Image ${idx + 1}`}
                                        onClick={() => window.open(imageUrl, '_blank')}
                                        title="Click to view full size"
                                        onError={(e) => {
                                          console.error('Image failed to load:', imageUrl);
                                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
                                        }}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="repair-actions">
                          <button 
                            className="btn btn-secondary"
                            onClick={() => handleEditRepair(repair)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-danger"
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this repair?')) {
                                handleDeleteRepair(repair.id);
                              }
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Vacant Quarters Popup */}
      {showVacantPopup && (
        <div className="popup-overlay" onClick={() => setShowVacantPopup(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h2>Vacant Quarters ({quarters.filter(q => isQuarterVacant(q)).length})</h2>
              <button 
                className="popup-close-btn" 
                onClick={() => setShowVacantPopup(false)}
              >
                ×
              </button>
            </div>
            
            <div className="popup-body">
              {quarters.filter(q => isQuarterVacant(q)).length === 0 ? (
                <p className="no-items">All quarters are currently occupied.</p>
              ) : (
                <div className="vacant-quarters-list">
                  {quarters
                    .filter(q => isQuarterVacant(q))
                    .sort((a, b) => {
                      // Sort by block first, then by quarter number
                      if (a.block !== b.block) {
                        return a.block.localeCompare(b.block);
                      }
                      return a.quarter_no.localeCompare(b.quarter_no);
                    })
                    .map(quarter => (
                      <div key={quarter.id} className="vacant-quarter-card">
                        <div className="vacant-quarter-info">
                          <h4>{quarter.quarter_no}</h4>
                          <div className="quarter-details">
                            <p>
  <strong>Block:</strong>{" "}
  {(() => {
    const blockLetter = quarter.block;
    const index = blockLetter.charCodeAt(0) - 65;
    return blockNamesList[index] || blockLetter;
  })()}
</p>
                            <p><strong>Type:</strong> {quarter.type}</p>
                            {quarter.rank && <p><strong>Rank:</strong> {quarter.rank}</p>}
                          </div>
                        </div>
                        <button
                          className="btn btn-small btn-primary"
                          onClick={() => {
                            handleEditQuarter(quarter);
                            setShowVacantPopup(false);
                          }}
                        >
                          Edit
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
